import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LANDING_CTA_URL, LANDING_SIGNIN_URL, loginReturnToLanding } from '../utils/ctaUrl';

const STORAGE_KEY = 'mdai_ref';
// Tight whitelist for the value we persist / forward, so a tampered `?ref=`
// can't be used to inject arbitrary URL fragments. Codes are 4–40 chars,
// alphanumerics + `-` / `_`. Anything else is dropped silently.
const VALID = /^[A-Za-z0-9_-]{4,40}$/;

function read() {
  if (typeof window === 'undefined') return '';
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

function persist(code) {
  if (typeof window === 'undefined' || !code) return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, code);
  } catch { /* ignore */ }
}

/**
 * Returns the current referral context for the landing page.
 *
 *   const { code, ctaUrl, signInUrl } = useReferralCta();
 *
 * - `code`     — the validated `?ref=` from the URL or sessionStorage, or ''.
 * - `ctaUrl`   — destination for "Get started" / "Start assessment" / etc.
 *                When a code is present, this is forced to `${LANDING_CTA_URL}/register?ref=CODE`
 *                so the portal's signup page can attach the referrer. Otherwise
 *                falls back to the configured LANDING_CTA_URL.
 * - `signInUrl`— destination for "Sign in". Same param-preservation pattern.
 *
 * Codes are validated against a strict whitelist before being attached to a URL,
 * so a forged `?ref=` can't be used to inject query strings or path segments.
 */
export function useReferralCta() {
  const location = useLocation();
  const [code, setCode] = useState(() => read());

  // On every URL change, look for a fresh `?ref=` and persist it. Sticking
  // it in sessionStorage means the visitor can browse the marketing pages
  // and still get credited when they finally click "Get started".
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incoming = (params.get('ref') || '').trim();
    if (incoming && VALID.test(incoming) && incoming !== code) {
      persist(incoming);
      setCode(incoming);
    }
  }, [location.search, code]);

  const value = useMemo(() => {
    // Client 2026-08-13: after signing in from the landing page the user
    // should be dropped back onto the landing page they came from — not
    // the portal dashboard. We attach `?from=<current landing URL>` and
    // rely on the portal's Login page (which honours trusted absolute
    // URLs via isTrustedAbsoluteUrl) to hop back cross-origin after auth.
    const landingReturnPath = (location.pathname || '/') + (location.search || '');
    const signInWithReturn = typeof window !== 'undefined'
      ? loginReturnToLanding(landingReturnPath)
      : LANDING_SIGNIN_URL;

    if (!code) {
      return { code: '', ctaUrl: LANDING_CTA_URL, signInUrl: signInWithReturn };
    }
    // Strip trailing slashes on the configured base so the join is clean.
    const base = LANDING_CTA_URL.replace(/\/+$/, '');
    const safeCode = encodeURIComponent(code);
    return {
      code,
      ctaUrl: `${base}/register?ref=${safeCode}`,
      signInUrl: signInWithReturn,
    };
  }, [code, location.pathname, location.search]);

  return value;
}
